import type { Src, TPlayer } from 'video.js'

import videojs from 'video.js'
import { Events } from '|>/shared/events'
import { getXhr } from '|>/shared/utils/vhs'

//
// Video.js plugins guide: https://videojs.com/guides/plugins/
//

const BasePlugin: Src.Plugin = videojs.getPlugin('plugin') as Src.Plugin

/**
 * Applies custom HTTP headers from the current source to VHS manifest/segment requests.
 */
export class PlaybackHeadersPlugin extends BasePlugin {
  declare player: TPlayer

  headers?: Record<string, string>

  constructor(player: TPlayer) {
    super(player)

    player.on('xhr-hooks-ready', this.enable)
    player.on(Events.Media.Load, this.onSourceLoad)
    player.on(['playerreset', 'dispose'], this.reset)
  }

  onSourceLoad = (_event: unknown, source?: Events.Media.Load) => {
    this.setHeaders(source?.headers)
  }

  setHeaders(headers?: Record<string, string>) {
    this.headers =
      headers && Object.keys(headers).length > 0 ? headers : undefined
  }

  enable = () => {
    getXhr(this.player, 'PlaybackHeaders plugin')?.onRequest(
      this.xhrRequestHook
    )

    // xhr-hooks-ready can fire after Events.Media.Load
    if (this.headers == null) {
      this.setHeaders(this.player.cachedSource?.headers)
    }
  }

  disable = () => {
    getXhr(this.player, 'PlaybackHeaders plugin')?.offRequest(
      this.xhrRequestHook
    )
  }

  xhrRequestHook = (options: {
    beforeSend?: (xhr: XMLHttpRequest) => void
  }) => {
    if (!this.headers) return options

    const headers = this.headers
    const prev = options.beforeSend

    options.beforeSend = (xhr: XMLHttpRequest) => {
      prev?.(xhr)
      for (const h in headers) {
        xhr.setRequestHeader(h, headers[h])
      }
    }

    return options
  }

  reset = () => {
    this.headers = undefined
  }

  override dispose() {
    this.disable()

    const player = this.player
    player.off('xhr-hooks-ready', this.enable)
    player.off(Events.Media.Load, this.onSourceLoad)
    player.off(['playerreset', 'dispose'], this.reset)

    super.dispose()
  }
}

videojs.registerPlugin('playbackHeaders', PlaybackHeadersPlugin)
